// Contract
export const POINTS_RIGHTS_DUE_DATE = 2027; // Default value for pointsRightsDueDate

// Explorer membership purchase prices
export const EXPLORER_PURCHASE_PRICES: Record<string, number> = {
    ["Explorer 1 Bed"]: 3595,
    ["Explorer 2 Bed"]: 4595,
};

// Helper to check if a membership level is an Explorer type
export const isExplorerMembership = (level?: string): boolean => {
    return level === MembershipLevel.Explorer_1_Bed || level === MembershipLevel.Explorer_2_Bed;
};

export interface Contract {
    _id: number;
    agreementNumber: number | null;//!donotchange
    adobeAgreementId?: number;//!donotchange

    // Owner
    owner: Owner; // The owner of the contract
    additionalContacts?: Contact[];

    // Contract Details
    purchasePrice: number;
    pointPrice: number;
    paymentTerms: string; // Monthly, Quarterly, Annually
    firstYearUse: number;
    termYears: number;
    pointsRightsPurchased: number;
    pointsRightsDueDate: number;
    paymentInstallments?: PaymentInstallments[]; // Optional
    date?: string; // Optional
    contractDate?: string; // Contract date field

    // Notes
    notes: Notes; // Grouped notes

    // Bookings
    bookings?: Booking[];

    // Documents
    documents?: ContractDocument[];

    // Form of surrender entries
    formOfSurrenders?: FormOfSurrender[];

    // Status
    status?: ContractStatus;
    welcomeEmailStatus?: WelcomeEmailStatus; // Email status (sent, pending, failed, etc.)
    // welcomeEmailStatus?: WelcomeEmailStatus; // DEPRECATED - replaced with offerConfirmationEmailStatus
    offerConfirmationEmailStatus?: OfferConfirmationEmailStatus;
    adobeStatus?: string; // Changed from enum to string

    // Settings
    //isNoBookingRequired?: boolean;
    // isSendWelcomeEmail?: boolean; // DEPRECATED - replaced with isSendOfferConfirmationEmail
    // isSendWelcomeEmailDraft?: boolean; // DEPRECATED

    emailVerifiedAt?: string; // Timestamp when email was verified
    isSendOfferConfirmationEmail?: boolean;
    isAttachJointMemberForm?:boolean;
    isTestSubmission?: boolean;
    archiveStatus?: string;
    userHistory?: UserHistory; // Tracks actions performed by users
    isRealEstateOwner?: boolean; // Changed from string to boolean
    newPointsPurchased?: number; // For tracking updates to points purchased
}

// Form of surrender entry - a contract can have multiple
export interface FormOfSurrender {
    _id?: number;
    agreementNumber: string;
    product: string;
    week?: number;
    points?: number;
}

// Owner
export interface Owner extends Contact {
    contactId?: number; // Optional: Contact ID for the owner
    membershipNumber: number | null;//!donotchange
    membershipLevel: MembershipLevel;
    address: Address;
    phone?: string;
    emailVerifiedAt?: string; // Timestamp when email was verified
    prospectId?: number; // Prospect ID for Explorer memberships
}

// Contact Information
export interface Contact {
    _id?: number; // Optional: Contact ID for the owner
    title?: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    email?: string;
    isJointMember?: boolean;
}

// Address
export interface Address {
    country: Country;
    postCode: string;
    stateProvince: string;
    town: string;
    buildingNumber: string;
    street: string;
}

// Payment Installment
export interface PaymentInstallments {
    date: string;
    amount: number;
}

// Booking
export interface Booking {
    _id?: number;
    contractId: number;
    unitDetailId?: number; // Reference to the unit detail
    unitNumber: string;
    resort: string;
    type?: string;
    location?: string; // Added location field
    week: number; // Changed from allowing string|number to only number
    years: number[];
    status: BookingStatus | number; // Allow both string enum and numeric values
    isCustom?: boolean; // Optional: Custom booking for not updating unit data
    tableName?: string; // Optional for custom bookings
    createdAt?: string;
    updatedAt?: string;
}

// Contract Document
export interface ContractDocument {
    _id: number;
    contractId: number;
    originalName: string;
    mimeType: string;
    fileSize: number;
    documentType?: string;
    uploadedBy?: {
        name: string;
        role: string;
    };
    uploadedAt: string;
}

// Settings
export interface Notes {
    contractNotes?: string; // Notes specific to the contract
    welcomeEmailNotes?: string; // Notes for the welcome/offer confirmation email
    offerConfirmationEmailNotes?: string; // Notes for the offer confirmation email
    internalNotes?: string; // Optional: Notes for internal use
    paymentNotes?: string; // Payment details notes
    formOfSurrenderNotes?: string; // Note global to the form of surrender section, not per entry
}

// Cart item for booking selection
export interface CartItem {
    roomNumber: string;
    unitNumber?: string;
    week: number;
    years: number[];
    resort: string;
    externalAvailabilityId?: string | number;
}

// Processed availability record for calendar display
export interface UnitAvailability {
    id?: string | number;  // external_availability_id from API
    resort: string;        // siteName from API
    number: string;        // roomNumber from API
    week: number;          // intervalNum from API (parsed)
    year: number;
    status: AvailabilityStatusText;
}

// Define the User interface
export interface User {
  _id: number;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  email: string;
  role: string;
}

// Define the UserHistory interface
export interface UserHistory {
    created?: UserAction; // Tracks the user who created the entity
    updated?: UserAction; // Tracks the user who last updated the entity
    deleted?: UserAction | null; // Tracks the user who deleted the entity (or null if not deleted)
    archived?: UserAction; // Tracks the user who archived the entity
  }

  // Define the UserAction interface
  export interface UserAction {
    name: string; // Name of the user
    role: string; // Role of the user (e.g., manager, admin)
    timestamp?: string; // Optional: Timestamp of the action
  }

// Enums
export enum Resort {
    MARINA_DEL_SOL = "Marina del Sol",
    MARINA_DORADA = "Marina Dorada",
    SIERRA_MARINA = "Sierra Marina",
    MARINA_PARK = "Marina Park",
    PUEBLO_MARINA = "Pueblo Marina",
    MARINA_DEL_REY = "Marina del Rey",
    CASTILLO_DEL_REY = "Castillo del Rey",
    SAN_DIEGO_SUITES = "San Diego Suites",
    SANTA_CRUZ_SUITES = "Santa Cruz Suites",
    SANTA_BARBARA_HEIGHTS = "Santa Barbara Heights",
    WYNDHAM_DUCHALLY = "Wyndham Duchally",
    WYNDHAM_RESIDENCES_GDS = "Wyndham Residences GDS",
    RAMADA_RESIDENCES_CA = "Ramada Residences CA",
    WYNDHAM_RESIDENCES_CA = "Wyndham Residences CA",
    ENCANTADA = "Encantada",
    REGAL_OAKS = "Regal Oaks"
}

export enum Country {
    UK = "United Kingdom",
    SPAIN = "Spain",
    GERMANY = "Germany",
    FRANCE = "France",
    ITALY = "Italy",
    NETHERLANDS = "Netherlands",
    BELGIUM = "Belgium",
    AUSTRIA = "Austria",
    IRELAND = "Ireland",
    FINLAND = "Finland",
    PORTUGAL = "Portugal",
    GREECE = "Greece",
    LUXEMBOURG = "Luxembourg",
    SLOVAKIA = "Slovakia",
    ESTONIA = "Estonia",
    LITHUANIA = "Lithuania",
    LATVIA = "Latvia",
    CYPRUS = "Cyprus",
    MALTA = "Malta",
    KUWAIT = "Kuwait",
    SWEDEN = "Sweden",
    NORWAY = "Norway",
    DENMARK = "Denmark",
    SWITZERLAND = "Switzerland",
    POLAND = "Poland",
    CZECH_REPUBLIC = "Czech Republic",
    HUNGARY = "Hungary",
    ROMANIA = "Romania",
    SLOVENIA = "Slovenia",
    CROATIA = "Croatia",
    BULGARIA = "Bulgaria",
    TURKEY = "Turkey",
    ISRAEL = "Israel",
    UNITED_STATES = "United States",
    CANADA = "Canada",
    AUSTRALIA = "Australia",
    NEW_ZEALAND = "New Zealand",
    CHINA = "China",
    HONG_KONG = "Hong Kong",
    SINGAPORE = "Singapore",
    UNITED_ARAB_EMIRATES = "United Arab Emirates",
    QATAR = "Qatar",
    SAUDI_ARABIA = "Saudi Arabia",
    INDIA = "India",
    JAPAN = "Japan",
    SOUTH_KOREA = "South Korea",
    SOUTH_AFRICA = "South Africa",
    BRAZIL = "Brazil",
    OMAN = "Oman"
}

// Utility Arrays
export const countryArray = Object.values(Country);
export const resortArray = Object.values(Resort);

// Add ContractStatus enum
export enum ContractStatus {
    New = "new",
    Pending = "pending",
    Active = "active",
    Complete = "complete",
    Archived = "archived",
}

// Add ContractStatus enum
export enum AvailabilityStatusText {
    Available = 0,
    Reserved = 1,
    Booked = 2,
    Selected=3
}

export enum BookingStatus {
    Active = "active",
    Inactive = "inactive",
    Cancelled = "cancelled",
    Custom = "custom"
}

export enum WelcomeEmailStatus {
    Pending = "pending",
    Sent = "sent",
    Opened = "opened",
    Failed = "failed"
}
 

export enum AdobeStatus {
    InProgress = "in_progress",
    Waiting = "waiting_for_prefill",
    Completed = "completed",
    Canceled = "canceled",
    Archived = "archived",
    Draft = "draft",
    Sending = "sending"
}

export enum ArchiveStatus {
    Pending = "pending",
    Complete = "complete",
    Failed   = "failed"
}


export enum MembershipLevel {
    Explorer_1_Bed = "Explorer 1 Bed",
    Explorer_2_Bed = "Explorer 2 Bed",
    HOC_Topaz_Plus = "HOC Topaz Plus",
    HOC_Sapphire_Plus = "HOC Sapphire Plus",
    HOC_Emerald_Plus = "HOC Emerald Plus",
    HOC_Crown = "HOC Crown",
}

// Membership level point prices
export const MembershipLevelPrices: Record<MembershipLevel, number> = {
    [MembershipLevel.HOC_Topaz_Plus]: 1.30,
    [MembershipLevel.HOC_Sapphire_Plus]: 1.20,
    [MembershipLevel.HOC_Emerald_Plus]: 1.05,
    [MembershipLevel.HOC_Crown]: 1.05,
    [MembershipLevel.Explorer_1_Bed]: 0.0,
    [MembershipLevel.Explorer_2_Bed]: 0.0
};

export enum Title {
    Mr = "Mr",
    Mrs = "Mrs",
    Miss = "Miss",
    Ms = "Ms",
    Dr = "Dr",
    Prof = "Prof",
    Rev = "Rev",
    Sir = "Sir",
}